import { Children, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import { SLIDER_PROPS } from '../types';
import { CarouselNextArrow } from '../utils/CarouselNextArrow';
import { CarouselPrevArrow } from '../utils/CarouselPrevArrow';

export const SimpleCarousal = ({
  afterChange,
  slidesToShow = 6,
  slidesToScroll = 1,
  vertical = false,
  arrows = false,
  infinite = false,
  autoplay = false,
  adaptiveHeight = true,
  customArrows = {
    customNextArrow: null,
    customPrevArrow: null,
  },
  dots = true,
  dotsClass = 'slick-dotss',
  mobile = {
    dots: true,
    arrows: false,
    centerMode: false,
    slidesToScroll: 1,
    slidesToShow: 1,
    infinite: true,
    autoplay: true,
  },
  mediumScreen = {
    slidesToShow: 2,
    slidesToScroll: 1,
    centerMode: false,
  },
  customInfinite = false,
  autoplaySpeed = 5000,
  children,
}: SLIDER_PROPS): JSX.Element => {
  const { customNextArrow, customPrevArrow } = customArrows;
  const {
    dots: mobile_dots = true,
    arrows: mobile_arrows = true,
    centerMode: mobile_centerMode = true,
    slidesToShow: mobile_slidesToShow = 1,
    slidesToScroll: mobile_slidesToScroll = 1,
  } = mobile;
  const {
    slidesToShow: mediumScreen_slidesToShow = 2,
    slidesToScroll: mediumScreen_slidesToScroll = 1,
    centerMode: mediumScreen_centerMode = false,
  } = mediumScreen;
  const [slickSlider, setSlickSlider] = useState<Slider | null>(null);
  const settings = {
    className: 'center',
    adaptiveHeight: adaptiveHeight,
    centerMode: false,
    dots: dots,
    dotsClass: dotsClass,
    infinite: infinite,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToScroll,
    vertical: vertical,
    initialSlide: 0,
    lazyload: 'progressive',
    waitForAnimate: false,
    arrows: arrows,
    autoplaySpeed: autoplaySpeed,
    pauseOnHover: true,
    pauseOnDotsHover: true,
    centerPadding: '250px',
    afterChange: afterChange
      ? afterChange
      : function (currentSlide: number) {
          if (!customInfinite) return;
          if (
            currentSlide + Math.floor(slidesToShow) ===
            Children.count(children)
          ) {
            setTimeout(() => {
              if (slickSlider) slickSlider.slickGoTo(0);
            }, autoplaySpeed);
          }
        },
    nextArrow: customNextArrow ?? <CarouselNextArrow classes="px-10" />,
    prevArrow: customPrevArrow ?? <CarouselPrevArrow classes="px-10" />,
    autoplay: autoplay,
    responsive: [
      {
        breakpoint: 1600,
        settings: {
          slidesToShow: slidesToShow,
          slidesToScroll: slidesToScroll,
          centerPadding: '150px',
          nextArrow: customNextArrow ?? <CarouselNextArrow classes="px-20" />,
          prevArrow: customPrevArrow ?? <CarouselPrevArrow classes="px-20" />,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: mediumScreen_slidesToShow,
          slidesToScroll: mediumScreen_slidesToScroll,
          centerPadding: '60px',
          nextArrow: customNextArrow ?? <CarouselNextArrow classes="px-9" />,
          prevArrow: customPrevArrow ?? <CarouselPrevArrow classes="px-9" />,
          centerMode: mediumScreen_centerMode,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: mobile_slidesToShow,
          slidesToScroll: mobile_slidesToScroll,
          centerPadding: '60px',
          nextArrow: customNextArrow ?? <CarouselNextArrow classes="px-9" />,
          prevArrow: customPrevArrow ?? <CarouselPrevArrow classes="px-9" />,
          dots: mobile_dots,
          arrows: mobile_arrows,
          centerMode: mobile_centerMode,
          afterChange: afterChange
            ? afterChange
            : function () {
                // do nothing
              },
          infinite: mobile.infinite,
          autoplay: mobile.autoplay,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: mobile_slidesToShow,
          slidesToScroll: mobile_slidesToScroll,
          nextArrow: customNextArrow ?? <div></div>,
          prevArrow: customPrevArrow ?? <div></div>,
          dots: mobile_dots,
          arrows: mobile_arrows,
          afterChange: afterChange
            ? afterChange
            : function () {
                // do nothing
              },
          infinite: mobile.infinite,
          autoplay: mobile.autoplay,
        },
      },
    ],
  };
  return (
    <Slider
      {...(settings as unknown as Settings)}
      ref={(slider) => setSlickSlider(slider)}
    >
      {children}
    </Slider>
  );
};
